/**
 * FanChart — P10/P50/P90 confidence bands around concentration trends.
 *
 * Native hand-rolled SVG (no Recharts). Recipe follows the BumpChart /
 * MovingAverageChart rebuilds:
 *   - d3-scale + d3-shape (area + line + curveMonotoneX) for math
 *   - useChartView → rectangle zoom + dblclick reset on the SVG body
 *   - <ChartTimeRangeRow> preset row above the chart
 *   - <ChartTooltip> on hover, multi-series rows
 *   - Regulatory MCL lines with the dv-mcl-glow filter
 *   - <clipPath> on the data layer so zoom never spills over the axis labels
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { area as d3Area, line as d3Line, curveMonotoneX } from 'd3-shape';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WellSelectorPanel } from '../../WellSelectorPanel';
import { AnalyteSelectorPanel } from '../../AnalyteSelectorPanel';
import { WELL_COLORS, ANALYTE_COLORS, hexToRgba } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { ChartTimeRangeRow } from '../../ChartTimeRangeRow';
import { useRegulatoryLines } from '../../../hooks/useRegulatoryLines';
import { defaultHiddenWellIds, defaultHiddenAnalytes } from '../../chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface BandPoint {
  ts: number;
  p10: number | null;
  p50: number | null;
  p90: number | null;
}

interface SeriesEntry {
  label: string;
  color: string;
  points: BandPoint[];
}

const MARGIN = { top: 12, right: 24, bottom: 36, left: 64 };
const CHART_HEIGHT = 420;
const WINDOW_SIZE = 5;

function percentile(arr: number[], p: number): number {
  const idx = (p / 100) * (arr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return arr[lo];
  return arr[lo] + (arr[hi] - arr[lo]) * (idx - lo);
}

function rollingPercentiles(
  sorted: Array<{ ts: number; v: number }>,
): BandPoint[] {
  const out: BandPoint[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = Math.max(0, i - WINDOW_SIZE + 1);
    const window = sorted.slice(start, i + 1).map((p) => p.v).sort((a, b) => a - b);
    if (window.length === 0) {
      out.push({ ts: sorted[i].ts, p10: null, p50: null, p90: null });
      continue;
    }
    out.push({
      ts: sorted[i].ts,
      p10: percentile(window, 10),
      p50: percentile(window, 50),
      p90: percentile(window, 90),
    });
  }
  return out;
}

export function FanChart({ data, unit }: Props) {
  // FanChart compares wells × analytes simultaneously — default to showing all.
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Set<string> | null>(null);

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound], i) => ({
      code,
      compound,
      color: ANALYTE_COLORS[i % ANALYTE_COLORS.length],
    }));
  }, [data.wells]);

  const effectiveHidden = useMemo(() => {
    if (hiddenAnalytes !== null) return hiddenAnalytes;
    return new Set<string>();
  }, [hiddenAnalytes]);

  const visibleAnalyteCodes = useMemo(
    () => new Set(allAnalytes.filter((a) => !effectiveHidden.has(a.code)).map((a) => a.code)),
    [allAnalytes, effectiveHidden],
  );

  const visibleWells = data.wells.filter((w) => !hiddenWellIds.has(w.well_id));
  const { lines: regulatoryLines } = useRegulatoryLines(data, visibleAnalyteCodes);

  const series: SeriesEntry[] = useMemo(() => {
    const out: SeriesEntry[] = [];
    for (const well of visibleWells) {
      const wellIdx = data.wells.findIndex((w) => w.well_id === well.well_id);
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!visibleAnalyteCodes.has(code)) continue;
        const analyteItem = allAnalytes.find((a) => a.code === code);
        const color = visibleWells.length <= 1
          ? (analyteItem?.color || ANALYTE_COLORS[0])
          : WELL_COLORS[wellIdx % WELL_COLORS.length];

        const sorted = analyte.measurements
          .filter((m) => m.concentration !== null)
          .map((m) => ({ ts: new Date(m.date).getTime(), v: convertConc(m.concentration, unit)! }))
          .sort((a, b) => a.ts - b.ts);
        if (sorted.length === 0) continue;
        const pts = rollingPercentiles(sorted);
        out.push({ label: `${well.well_name} — ${analyte.compound}`, color, points: pts });
      }
    }
    return out;
  }, [visibleWells, visibleAnalyteCodes, data.wells, allAnalytes, unit]);

  // Refs + responsive width
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `fan-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  // Base domains
  const baseXDomain = useMemo<readonly [number, number]>(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of series) {
      for (const p of s.points) {
        if (p.ts < lo) lo = p.ts;
        if (p.ts > hi) hi = p.ts;
      }
    }
    if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
    if (lo === hi) return [lo - 86_400_000, hi + 86_400_000];
    return [lo, hi];
  }, [series]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    let yMax = 0;
    for (const s of series) {
      for (const p of s.points) {
        if (p.p90 !== null && p.p90 > yMax) yMax = p.p90;
      }
    }
    for (const line of regulatoryLines) {
      if (line.value > yMax) yMax = line.value;
    }
    return [0, (yMax > 0 ? yMax : 1) * 1.1];
  }, [series, regulatoryLines]);

  // View hook
  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  // Generators
  const bandGen = useMemo(
    () =>
      d3Area<BandPoint>()
        .defined((p) => p.p10 !== null && p.p90 !== null)
        .x((p) => xScale(p.ts))
        .y0((p) => yScale(p.p10 as number))
        .y1((p) => yScale(p.p90 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );
  const p50Gen = useMemo(
    () =>
      d3Line<BandPoint>()
        .defined((p) => p.p50 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.p50 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );
  const p10Gen = useMemo(
    () =>
      d3Line<BandPoint>()
        .defined((p) => p.p10 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.p10 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );
  const p90Gen = useMemo(
    () =>
      d3Line<BandPoint>()
        .defined((p) => p.p90 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.p90 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  // Hover — nearest sample timestamp across all series
  interface HoverState { ts: number; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      let bestTs: number | null = null;
      let bestDx = Infinity;
      for (const s of series) {
        for (const p of s.points) {
          const px = xScale(p.ts);
          const dx = Math.abs(px - localX);
          if (dx < bestDx) {
            bestDx = dx;
            bestTs = p.ts;
          }
        }
      }
      if (bestTs !== null && bestDx < 30) {
        setHover({ ts: bestTs, clientX: e.clientX, clientY: e.clientY });
      } else {
        setHover(null);
      }
    },
    [series, xScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  const hoverRows = useMemo(() => {
    if (!hover) return [];
    const rows: Array<{ label: string; color: string; p10: number | null; p50: number | null; p90: number | null }> = [];
    for (const s of series) {
      const point = s.points.find((p) => p.ts === hover.ts);
      if (point) {
        rows.push({ label: s.label, color: s.color, p10: point.p10, p50: point.p50, p90: point.p90 });
      }
    }
    return rows;
  }, [hover, series]);

  // Empty state
  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));
  const visibleWellIdSet = new Set(visibleWells.map((w) => w.well_id));

  return (
    <div className="space-y-3">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Fan chart showing confidence bands (P10/P50/P90 envelopes) around concentration trends.
        The shaded bands widen where uncertainty is higher, helping assess data reliability.
      </p>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map((w) => w.well_id)))}
      />

      <AnalyteSelectorPanel
        analytes={allAnalytes}
        visibleCodes={visibleAnalyteCodes}
        onToggle={(code) => {
          const next = new Set(effectiveHidden);
          if (next.has(code)) next.delete(code);
          else next.add(code);
          setHiddenAnalytes(next);
        }}
        onSelectAll={() => setHiddenAnalytes(new Set())}
        onDeselectAll={() => setHiddenAnalytes(new Set(allAnalytes.map((a) => a.code)))}
      />

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef} role="img" aria-label="Fan chart of concentration uncertainty ranges">
        {series.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT WELLS AND ANALYTES TO DISPLAY
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
              {series.map((s, i) => (
                <linearGradient key={`grad-${i}`} id={`${clipId}-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>

            <g clipPath={`url(#${clipId})`}>
              {/* Gridlines */}
              {yTicks.map((t) => {
                const y = yScale(t);
                return (
                  <line
                    key={`grid-${t}`}
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke="var(--dv-chart-grid)"
                    strokeDasharray="3 3"
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                );
              })}

              {/* Regulatory lines */}
              {regulatoryLines.map((line) => {
                const v = convertConc(line.value, unit);
                if (v === null) return null;
                const [yLo, yHi] = view.yDomain;
                if (v > yHi || v < yLo) return null;
                const y = yScale(v);
                const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
                return (
                  <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                    <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                    <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {`${line.label} ${line.analyteName}`}
                    </text>
                  </g>
                );
              })}

              {/* Band + boundary lines + median, per series */}
              {series.map((s, i) => (
                <g key={`series-${i}`}>
                  <path d={bandGen(s.points) ?? ''} fill={`url(#${clipId}-grad-${i})`} stroke="none" />
                  <path d={p10Gen(s.points) ?? ''} fill="none" stroke={hexToRgba(s.color, 0.45)} strokeWidth={1} strokeDasharray="4 3" />
                  <path d={p90Gen(s.points) ?? ''} fill="none" stroke={hexToRgba(s.color, 0.45)} strokeWidth={1} strokeDasharray="4 3" />
                  <path d={p50Gen(s.points) ?? ''} fill="none" stroke={s.color} strokeWidth={2.25} strokeLinejoin="round" />
                  {s.points.map((p, j) => {
                    if (p.p50 === null) return null;
                    const isHovered = hover?.ts === p.ts;
                    return (
                      <circle
                        key={`dot-${i}-${j}`}
                        cx={xScale(p.ts)}
                        cy={yScale(p.p50)}
                        r={isHovered ? 4.5 : 3}
                        fill={s.color}
                        stroke="var(--dv-bg-card)"
                        strokeWidth={isHovered ? 2 : 1}
                        style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                      />
                    );
                  })}
                </g>
              ))}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}

            {/* Rect-zoom preview */}
            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            {/* Hit overlay */}
            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && hoverRows.length > 0 && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 220 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.04em',
                color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))',
                textTransform: 'uppercase',
                marginBottom: 4,
                paddingBottom: 4,
                borderBottom: '1px solid var(--dv-tooltip-border, rgba(255,255,255,0.08))',
              }}
            >
              {new Date(hover.ts).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11 }}>
              {hoverRows.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: r.color,
                    }}
                  />
                  <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{r.label}</span>
                  <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                    {r.p50 !== null ? r.p50.toFixed(3) : '—'}
                  </span>
                  <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))', fontSize: 10 }}>
                    [{r.p10 !== null ? r.p10.toFixed(2) : '—'}–{r.p90 !== null ? r.p90.toFixed(2) : '—'}]
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
